import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditActivityFeed = ({ activities = [], onLoadMore, hasMore = false }) => {
  const [filter, setFilter] = useState('all');

  const getActivityIcon = (type) => {
    const iconConfig = {
      correction: { icon: 'Edit', color: 'text-primary' },
      approval: { icon: 'Check', color: 'text-success' },
      rejection: { icon: 'X', color: 'text-error' },
      creation: { icon: 'Plus', color: 'text-accent' },
      deletion: { icon: 'Trash2', color: 'text-error' },
      export: { icon: 'Download', color: 'text-secondary' },
      login: { icon: 'LogIn', color: 'text-text-secondary' },
      system: { icon: 'Settings', color: 'text-warning' }
    };

    const config = iconConfig?.[type] || { icon: 'Clock', color: 'text-text-secondary' };
    return (
      <div className={`w-8 h-8 rounded-full bg-muted flex items-center justify-center ${config?.color}`}>
        <Icon name={config?.icon} size={16} />
      </div>
    );
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Gerade eben';
    if (diffInMinutes < 60) return `vor ${diffInMinutes} Min.`;
    if (diffInMinutes < 1440) return `vor ${Math.floor(diffInMinutes / 60)} Std.`;
    
    return date?.toLocaleDateString('de-DE', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityDescription = (activity) => {
    const { type, details, user, target } = activity;
    
    switch (type) {
      case 'correction':
        return `${user?.name} korrigierte den Zeiteintrag für ${target?.employee}`;
      case 'approval':
        return `${user?.name} genehmigte die Korrektur für ${target?.employee}`;
      case 'rejection':
        return `${user?.name} lehnte die Korrektur für ${target?.employee} ab`;
      case 'creation':
        return `${user?.name} erstellte einen manuellen Eintrag für ${target?.employee}`;
      case 'deletion':
        return `${user?.name} löschte den Eintrag für ${target?.employee}`;
      case 'export':
        return `${user?.name} exportierte einen Zeiteinträge-Bericht`;
      case 'login':
        return `${user?.name} hat sich im System angemeldet`;
      case 'system':
        return `System: ${details?.message}`;
      default:
        return `${user?.name} führte die Aktion "${type}" aus`;
    }
  };

  const getActivityDetails = (activity) => {
    const { type, details } = activity;
    
    if (type === 'correction' && details?.changes) {
      return (
        <div className="text-xs text-text-secondary mt-1">
          {details?.changes?.clockIn && (
            <div>Kommen: {details?.changes?.clockIn?.from} → {details?.changes?.clockIn?.to}</div>
          )}
          {details?.changes?.clockOut && (
            <div>Gehen: {details?.changes?.clockOut?.from} → {details?.changes?.clockOut?.to}</div>
          )}
          {details?.reason && (
            <div className="mt-1 italic">Grund: {details?.reason}</div>
          )}
        </div>
      );
    }

    if (details?.message) {
      return (
        <div className="text-xs text-text-secondary mt-1">
          {details?.message}
        </div>
      );
    }

    return null;
  };

  const filterOptions = [
    { value: 'all', label: 'Alle Aktivitäten', count: activities?.length },
    { value: 'correction', label: 'Korrekturen', count: activities?.filter(a => a?.type === 'correction')?.length },
    { value: 'approval', label: 'Genehmigungen', count: activities?.filter(a => a?.type === 'approval')?.length },
    { value: 'system', label: 'Systemereignisse', count: activities?.filter(a => a?.type === 'system')?.length }
  ];
  
  const filterLabels = {
    all: 'Aktivitäten',
    correction: 'Korrekturen',
    approval: 'Genehmigungen',
    system: 'Systemereignisse'
  };

  const filteredActivities = filter === 'all' 
    ? activities 
    : activities?.filter(activity => activity?.type === filter);

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Activity" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-text-primary">Aktuelle Aktivitäten</h3>
        </div>
        
        {/* Filter Tabs */}
        <div className="flex items-center space-x-1">
          {filterOptions?.map((option) => (
            <button
              key={option?.value}
              onClick={() => setFilter(option?.value)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                filter === option?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-text-secondary hover:text-text-primary hover:bg-muted'
              }`}
            >
              {option?.label} ({option?.count})
            </button>
          ))}
        </div>
      </div>
      {/* Activity Feed */}
      <div className="max-h-96 overflow-y-auto">
        {filteredActivities?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Clock" size={48} className="text-text-secondary mx-auto mb-4" />
            <h4 className="text-lg font-medium text-text-primary mb-2">Keine aktuellen Aktivitäten</h4>
            <p className="text-text-secondary">
              {filter === 'all' ? 'Es sind noch keine Audit-Aktivitäten zum Anzeigen vorhanden.'
                : `Keine ${filterLabels[filter] || filter} gefunden.`
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredActivities?.map((activity) => (
              <div key={activity?.id} className="p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-start space-x-3">
                  {getActivityIcon(activity?.type)}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-text-primary">
                        {getActivityDescription(activity)}
                      </p>
                      <span className="text-xs text-text-secondary whitespace-nowrap ml-2">
                        {formatTimestamp(activity?.timestamp)}
                      </span>
                    </div>
                    
                    {getActivityDetails(activity)}
                    
                    {activity?.ipAddress && (
                      <div className="text-xs text-text-secondary mt-1">
                        IP: {activity?.ipAddress}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Load More */}
      {hasMore && (
        <div className="p-4 border-t border-border text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            iconName="ChevronDown"
            iconPosition="left"
            className="text-text-secondary hover:text-text-primary"
          >
            Weitere Aktivitäten laden
          </Button>
        </div>
      )}
    </div>
  );
};

export default AuditActivityFeed;